import ChatService from "@/src/api/ChatService.js";

export default {
  namespaced: true,

  state: {
    newChat: {
      name: "",
      users: [],
    },
    threadStatus: {
      isOpen: false,
      messageID: null,
      threadId: null,
    },
    activeChat: null,
    messages: [],
    messagesLoading: false,
    messagesError: null,
    messagesPagination: null,
    threadMessages: [],
    threadMessagesLoading: false,
    threadMessagesError: null,
    threadMessagesPagination: null,
    pendingMessages: new Map(),
    pendingReactions: new Map(),
    groups: [],
    typingUsers: {},
    lastFetchedChatId: null,
    hasUnreadIndicator: false,
    messageSearchQuery: "",
    editingMessage: null,
    flashThreadMessageId: null,
    isCurrentUserGroupMember: true,
    groupMembershipCache: {},
    chatNotificationSoundEnabled: true,
    cachedTotalUnreadCount: 0,
    activeChatTab: "all",
    pinnedGroupIds: new Set(),
  },

  mutations: {
    SET_NEW_CHAT(state, chat) {
      state.newChat = chat;
    },

    ADD_USER_TO_CHAT(state, user) {
      const exists = state.newChat.users.find((u) => u.id === user.id);
      if (!exists) {
        state.newChat.users.push(user);
      }
    },

    REMOVE_USER_FROM_CHAT(state, userID) {
      state.newChat.users = state.newChat.users.filter((u) => u.id !== userID);
    },

    SET_CHAT_USERS(state, users) {
      state.newChat.users = users;
    },

    CLEAR_NEW_CHAT(state) {
      state.newChat = {
        name: "",
        users: [],
      };
    },

    SET_THREAD_STATUS(state, status) {
      state.threadStatus = status;
    },

    SET_THREAD_MESSAGE_ID(state, messageID) {
      state.threadStatus.messageID = messageID;
    },

    TOGGLE_THREAD(state, payload = {}) {
      if (typeof payload === "object") {
        const { messageID, threadId, forceState } = payload;
        if (forceState !== undefined) {
          state.threadStatus.isOpen = forceState;
          state.threadStatus.messageID = forceState
            ? messageID || state.threadStatus.messageID
            : null;
          state.threadStatus.threadId = forceState
            ? threadId || state.threadStatus.threadId
            : null;
        } else {
          state.threadStatus.isOpen = !state.threadStatus.isOpen;
          state.threadStatus.messageID = state.threadStatus.isOpen
            ? messageID || state.threadStatus.messageID
            : null;
          state.threadStatus.threadId = state.threadStatus.isOpen
            ? threadId || state.threadStatus.threadId
            : null;
        }
      } else {
        state.threadStatus.isOpen = !state.threadStatus.isOpen;
        state.threadStatus.messageID = state.threadStatus.isOpen
          ? payload || state.threadStatus.messageID
          : null;
      }
    },

    SET_ACTIVE_CHAT(state, chat) {
      state.activeChat = chat;
    },

    SET_FLASH_THREAD_MESSAGE_ID(state, messageId) {
      state.flashThreadMessageId = messageId;
    },
    SET_IS_CURRENT_USER_GROUP_MEMBER(state, isMember) {
      state.isCurrentUserGroupMember = isMember;
    },
    SET_GROUP_MEMBERSHIP(state, { groupId, isMember }) {
      state.groupMembershipCache = { ...state.groupMembershipCache, [groupId]: isMember };
    },
    SET_CHAT_NOTIFICATION_SOUND_ENABLED(state, enabled) {
      state.chatNotificationSoundEnabled = enabled;
    },

    SET_LAST_FETCHED_CHAT_ID(state, chatId) {
      state.lastFetchedChatId = chatId;
    },

    SET_UNREAD_INDICATOR(state, value) {
      state.hasUnreadIndicator = value;
    },

    SET_EDITING_MESSAGE(state, message) {
      state.editingMessage = message;
    },

    SET_MESSAGES(state, messages) {
      state.messages = messages;
    },

    SET_MESSAGES_LOADING(state, loading) {
      state.messagesLoading = loading;
    },

    SET_MESSAGES_ERROR(state, error) {
      state.messagesError = error;
    },

    SET_MESSAGES_PAGINATION(state, pagination) {
      state.messagesPagination = pagination;
    },

    CLEAR_MESSAGES(state) {
      state.messages = [];
      state.messagesError = null;
      state.messagesPagination = null;
      state.pendingMessages.clear();
      state.pendingReactions.clear();
    },

    SET_MESSAGE_SEARCH_QUERY(state, query) {
      state.messageSearchQuery = query ?? "";
    },

    SET_THREAD_MESSAGES(state, messages) {
      state.threadMessages = messages;
    },

    SET_THREAD_MESSAGES_LOADING(state, loading) {
      state.threadMessagesLoading = loading;
    },

    SET_THREAD_MESSAGES_ERROR(state, error) {
      state.threadMessagesError = error;
    },

    SET_THREAD_MESSAGES_PAGINATION(state, pagination) {
      state.threadMessagesPagination = pagination;
    },

    CLEAR_THREAD_MESSAGES(state) {
      state.threadMessages = [];
      state.threadMessagesError = null;
      state.threadMessagesPagination = null;
    },

    ADD_OPTIMISTIC_MESSAGE(state, message) {
      const isThreadReply = message.thread_id && message.thread_id !== message.id;
      if (isThreadReply) {
        state.threadMessages = [...state.threadMessages, message];
      } else {
        state.messages = [...state.messages, message];
      }
      state.pendingMessages.set(message.id, {
        timestamp: Date.now(),
        text: message.text,
      });
    },

    ADD_PENDING_MESSAGE_ID(state, messageId) {
      state.pendingMessages.set(messageId, {
        timestamp: Date.now(),
      });
      
      setTimeout(() => {
        state.pendingMessages.delete(messageId);
      }, 5000);
    },

    REPLACE_OPTIMISTIC_MESSAGE(state, { tempId, realMessage }) {
      const isThreadReply = realMessage.thread_id && realMessage.thread_id !== realMessage.id;
      
      if (isThreadReply) {
        const index = state.threadMessages.findIndex((msg) => msg.id === tempId);
        if (index !== -1) {
          const updatedMessages = [...state.threadMessages];
          updatedMessages[index] = realMessage;
          state.threadMessages = updatedMessages;
        }
      } else {
        const index = state.messages.findIndex((msg) => msg.id === tempId);
        if (index !== -1) {
          const updatedMessages = [...state.messages];
          updatedMessages[index] = realMessage;
          state.messages = updatedMessages;
        }
      }
      state.pendingMessages.delete(tempId);
    },

    REMOVE_OPTIMISTIC_MESSAGE(state, tempId) {
      const threadIndex = state.threadMessages.findIndex((msg) => msg.id === tempId);
      if (threadIndex !== -1) {
        state.threadMessages = state.threadMessages.filter((msg) => msg.id !== tempId);
      } else {
        state.messages = state.messages.filter((msg) => msg.id !== tempId);
      }
      state.pendingMessages.delete(tempId);
    },

    UPDATE_MESSAGE_REACTIONS(state, { messageId, emoji, increment }) {
      const index = state.messages.findIndex((msg) => msg.id == messageId);
      if (index !== -1) {
        const message = state.messages[index];
        const reactions = { ...(message.reactions || {}) };
        const currentCount = reactions[emoji] || 0;
        const newCount = currentCount + increment;

        if (newCount <= 0) {
          delete reactions[emoji];
        } else {
          reactions[emoji] = newCount;
        }

        const updatedMessages = [...state.messages];
        updatedMessages[index] = { ...message, reactions };
        state.messages = updatedMessages;
      }
    },

    ADD_PENDING_REACTION(state, { key, action }) {
      state.pendingReactions.set(key, {
        action,
        timestamp: Date.now(),
      });
    },

    REMOVE_PENDING_REACTION(state, key) {
      state.pendingReactions.delete(key);
    },

    ADD_MESSAGE(state, message) {
      // Don't pollute search results with incoming real-time messages
      if (state.messageSearchQuery) {
        return;
      }

      // Guard: don't add messages from a different group into the active chat's message list
      const messageGroupId = message.group_id || message.conversation_id;
      if (messageGroupId && state.activeChat?.id) {
        const normalizedMsgGroup = String(messageGroupId).replace(/^group_/, '');
        const normalizedActiveChat = String(state.activeChat.id).replace(/^group_/, '');
        if (normalizedMsgGroup !== normalizedActiveChat) {
          return;
        }
      } else if (messageGroupId && !state.activeChat?.id) {
        // No active chat set — reject realtime messages that specify a group
        return;
      }

      const exists = state.messages.find((m) => m.id == message.id);
      const isPending = state.pendingMessages.has(message.id);

      if (!exists && !isPending) {
        const newMessage = { ...message };

        if (newMessage.broadcast_from_thread_id && !newMessage._resolvedThreadRoot) {
          const replyMsg = state.threadMessages.find(m => m.id === newMessage.broadcast_from_thread_id);

          if (replyMsg && replyMsg.thread_id) {
            newMessage._resolvedThreadRoot = replyMsg.thread_id;
          }
        }

        state.messages = [...state.messages, newMessage];
      }
    },

    UPDATE_MESSAGE(state, message) {
      const index = state.messages.findIndex((m) => m.id == message.id);
      if (index !== -1) {
        const updatedMessages = [...state.messages];
        updatedMessages[index] = message;
        state.messages = updatedMessages;
      }
    },

    REMOVE_MESSAGE(state, messageId) {
      state.messages = state.messages.filter((m) => m.id != messageId);
    },

    UPDATE_BROADCAST_COPY(state, { threadReplyId, text, edited, edited_at, attachments }) {
      const index = state.messages.findIndex(
        (m) => m.broadcast_from_thread_id && String(m.broadcast_from_thread_id) === String(threadReplyId)
      );
      if (index !== -1) {
        const updatedMessages = [...state.messages];
        updatedMessages[index] = {
          ...updatedMessages[index],
          text,
          edited,
          edited_at,
          attachments: attachments || updatedMessages[index].attachments,
        };
        state.messages = updatedMessages;
      }
    },

    UPDATE_THREAD_COUNT(state, { threadId, replyCount, delta }) {
      const messageIndex = state.messages.findIndex((m) => m.thread_id == threadId);
      if (messageIndex !== -1) {
        const currentCount = state.messages[messageIndex].reply_count || 0;
        let newCount;
        if (replyCount !== null && replyCount !== undefined) {
          newCount = replyCount;
        } else if (delta !== undefined) {
          newCount = Math.max(0, currentCount + delta);
        } else {
          newCount = currentCount;
        }
        
        const updatedMessage = {
          ...state.messages[messageIndex],
          reply_count: newCount
        };
        const updatedMessages = [...state.messages];
        updatedMessages[messageIndex] = updatedMessage;
        state.messages = updatedMessages;
      }
    },

    ADD_THREAD_MESSAGE(state, message) {
      const exists = state.threadMessages.find((m) => m.id == message.id);
      const isPending = state.pendingMessages.has(message.id);
      
      if (!exists && !isPending) {
        state.threadMessages = [...state.threadMessages, message];
      }
    },

    UPDATE_THREAD_MESSAGE(state, message) {
      const index = state.threadMessages.findIndex((m) => m.id == message.id);
      if (index !== -1) {
        const updatedMessages = [...state.threadMessages];
        updatedMessages[index] = message;
        state.threadMessages = updatedMessages;
      }
    },

    REMOVE_THREAD_MESSAGE(state, messageId) {
      state.threadMessages = state.threadMessages.filter((m) => m.id != messageId);
    },

    UPDATE_THREAD_MESSAGE_REACTIONS(state, { messageId, emojiKey, delta }) {
      const index = state.threadMessages.findIndex((m) => m.id == messageId);
      if (index !== -1) {
        const message = state.threadMessages[index];
        const reactions = { ...(message.reactions || {}) };
        const currentCount = reactions[emojiKey] || 0;
        const newCount = currentCount + delta;

        if (newCount <= 0) {
          delete reactions[emojiKey];
        } else {
          reactions[emojiKey] = newCount;
        }

        const updatedMessages = [...state.threadMessages];
        updatedMessages[index] = { ...message, reactions };
        state.threadMessages = updatedMessages;
      }
    },

    SET_CACHED_TOTAL_UNREAD_COUNT(state, count) {
      state.cachedTotalUnreadCount = count;
    },

    SET_ACTIVE_CHAT_TAB(state, tab) {
      state.activeChatTab = tab;
    },

    SET_PINNED_GROUP_IDS(state, ids) {
      state.pinnedGroupIds = new Set(ids.map(String));
    },

    TOGGLE_PINNED_GROUP(state, { groupId, pinned }) {
      const id = String(groupId);
      if (pinned) {
        state.pinnedGroupIds = new Set([...state.pinnedGroupIds, id]);
      } else {
        const next = new Set(state.pinnedGroupIds);
        next.delete(id);
        state.pinnedGroupIds = next;
      }
    },

    SET_GROUPS(state, { groups, skipUnreadRecalc = false }) {
      const uniqueGroups = [];
      const seenIds = new Set();

      groups.forEach(group => {
        const normalizedId = String(group.id).replace(/^group_/, '');
        if (!seenIds.has(normalizedId)) {
          seenIds.add(normalizedId);
          uniqueGroups.push(group);
        }
      });

      state.groups = uniqueGroups;

      if (!skipUnreadRecalc) {
        state.cachedTotalUnreadCount = uniqueGroups.reduce(
          (sum, g) => sum + (g.unread_count || 0), 0
        );
      }
    },

    ADD_GROUP_TO_LIST(state, group) {
      if (!group) return;
      
      const normalizedNewGroupId = String(group.id).replace(/^group_/, '');
      const exists = state.groups.some((g) => {
        const gId = String(g.id).replace(/^group_/, '');
        return gId === normalizedNewGroupId;
      });
      
      if (!exists) {
        state.groups = [group, ...state.groups];
        state.cachedTotalUnreadCount += (group.unread_count || 0);
      }
    },

    UPDATE_SIDEBAR_GROUP(state, { groupId, lastMessage, lastMessageAttachments, senderId, lastMessageTime }) {
      const normalizedGroupId = String(groupId).replace(/^group_/, '');
      const groupIndex = state.groups.findIndex((g) => {
        const gId = String(g.id).replace(/^group_/, '');
        return gId === normalizedGroupId;
      });

      if (groupIndex !== -1) {
        const updatedGroup = {
          ...state.groups[groupIndex],
          last_message: lastMessage,
          last_message_time: lastMessageTime || new Date().toISOString()
        };
        
        if (lastMessageAttachments) {
          updatedGroup.last_message_attachments = lastMessageAttachments;
        }
        
        const otherGroups = state.groups.filter((g, idx) => idx !== groupIndex);
        state.groups = [updatedGroup, ...otherGroups];
      }
    },

    UPDATE_GROUP_INFO(state, { groupId, name, description, avatarUrl, profilePic }) {
      const normalizedGroupId = String(groupId).replace(/^group_/, '');
      const group = state.groups.find((g) => {
        const gId = String(g.id).replace(/^group_/, '');
        return gId === normalizedGroupId;
      });
      if (group) {
        if (name) group.name = name;
        if (description !== undefined) group.description = description;
        if (avatarUrl) group.avatar_url = avatarUrl;
        if (profilePic !== undefined) group.profile_pic = profilePic;
      }

      // Also update activeChat if it matches
      if (state.activeChat && String(state.activeChat.id).replace(/^group_/, '') === normalizedGroupId) {
        if (name) state.activeChat.name = name;
        if (description !== undefined) state.activeChat.description = description;
        if (avatarUrl) state.activeChat.avatar_url = avatarUrl;
        if (profilePic !== undefined) state.activeChat.profile_pic = profilePic;
      }
    },

    REMOVE_GROUP(state, groupId) {
      const normalizedGroupId = String(groupId).replace(/^group_/, '');
      const removedGroup = state.groups.find((g) => {
        const gId = String(g.id).replace(/^group_/, '');
        return gId === normalizedGroupId;
      });
      if (removedGroup) {
        state.cachedTotalUnreadCount = Math.max(0, state.cachedTotalUnreadCount - (removedGroup.unread_count || 0));
      }
      state.groups = state.groups.filter((g) => {
        const gId = String(g.id).replace(/^group_/, '');
        return gId !== normalizedGroupId;
      });
      
      if (state.activeChat?.id) {
        const activeChatId = String(state.activeChat.id).replace(/^group_/, '');
        if (activeChatId === normalizedGroupId) {
          state.activeChat = null;
        }
      }
    },

    UPDATE_UNREAD_COUNT(state, { groupId, unreadCount }) {
      const normalizedGroupId = String(groupId).replace(/^group_/, '');
      const group = state.groups.find((g) => {
        const gId = String(g.id).replace(/^group_/, '');
        return gId === normalizedGroupId;
      });
      if (group) {
        const oldCount = group.unread_count || 0;
        group.unread_count = unreadCount;
        state.cachedTotalUnreadCount = Math.max(0, state.cachedTotalUnreadCount - oldCount + (unreadCount || 0));
      }
    },

    SET_TYPING_USER(state, { groupId, userId }) {
      const normalizedGroupId = String(groupId).replace(/^group_/, '');
      state.typingUsers[normalizedGroupId] = { userId, timestamp: Date.now() };
    },

    CLEAR_TYPING_USER(state, groupId) {
      const normalizedGroupId = String(groupId).replace(/^group_/, '');
      delete state.typingUsers[normalizedGroupId];
    },
  },

  actions: {
    handleNewChat({ commit }, chat) {
      commit("SET_NEW_CHAT", chat);
    },

    addUserToChat({ commit }, user) {
      commit("ADD_USER_TO_CHAT", user);
    },

    removeUserFromChat({ commit }, userID) {
      commit("REMOVE_USER_FROM_CHAT", userID);
    },

    setChatUsers({ commit }, users) {
      commit("SET_CHAT_USERS", users);
    },

    clearNewChat({ commit }) {
      commit("CLEAR_NEW_CHAT");
    },

    setThreadStatus({ commit }, status) {
      commit("SET_THREAD_STATUS", status);
    },

    setThreadMessageID({ commit }, messageID) {
      commit("SET_THREAD_MESSAGE_ID", messageID);
    },

    toggleThread({ commit }, payload) {
      commit("TOGGLE_THREAD", payload);
    },

    setActiveChat({ commit }, chat) {
      commit("SET_ACTIVE_CHAT", chat);
      commit("TOGGLE_THREAD", { forceState: false });
    },

    setMessages({ commit }, messages) {
      commit("SET_MESSAGES", messages);
    },

    setMessagesLoading({ commit }, loading) {
      commit("SET_MESSAGES_LOADING", loading);
    },

    setMessagesError({ commit }, error) {
      commit("SET_MESSAGES_ERROR", error);
    },

    setMessagesPagination({ commit }, pagination) {
      commit("SET_MESSAGES_PAGINATION", pagination);
    },

    clearMessages({ commit }) {
      commit("CLEAR_MESSAGES");
    },

    setMessageSearchQuery({ commit }, query) {
      commit("SET_MESSAGE_SEARCH_QUERY", query);
    },

    setThreadMessages({ commit }, messages) {
      commit("SET_THREAD_MESSAGES", messages);
    },

    setThreadMessagesLoading({ commit }, loading) {
      commit("SET_THREAD_MESSAGES_LOADING", loading);
    },

    setThreadMessagesError({ commit }, error) {
      commit("SET_THREAD_MESSAGES_ERROR", error);
    },

    setThreadMessagesPagination({ commit }, pagination) {
      commit("SET_THREAD_MESSAGES_PAGINATION", pagination);
    },

    clearThreadMessages({ commit }) {
      commit("CLEAR_THREAD_MESSAGES");
    },

    addOptimisticMessage({ commit }, message) {
      commit("ADD_OPTIMISTIC_MESSAGE", message);
    },

    replaceOptimisticMessage({ commit }, { tempId, realMessage }) {
      commit("REPLACE_OPTIMISTIC_MESSAGE", { tempId, realMessage });
    },

    removeOptimisticMessage({ commit }, tempId) {
      commit("REMOVE_OPTIMISTIC_MESSAGE", tempId);
    },

    updateMessageReactions({ commit }, { messageId, emoji, increment }) {
      commit("UPDATE_MESSAGE_REACTIONS", { messageId, emoji, increment });
    },

    addPendingReaction({ commit }, { key, action }) {
      commit("ADD_PENDING_REACTION", { key, action });
    },

    removePendingReaction({ commit }, key) {
      commit("REMOVE_PENDING_REACTION", key);
    },

    addMessage({ commit }, message) {
      commit("ADD_MESSAGE", message);
    },

    updateMessage({ commit }, message) {
      commit("UPDATE_MESSAGE", message);
    },

    removeMessage({ commit }, messageId) {
      commit("REMOVE_MESSAGE", messageId);
    },

    updateBroadcastCopy({ commit }, payload) {
      commit("UPDATE_BROADCAST_COPY", payload);
    },

    updateThreadCount({ commit }, { threadId, replyCount, delta }) {
      commit("UPDATE_THREAD_COUNT", { threadId, replyCount, delta });
    },

    addThreadMessage({ commit }, message) {
      commit("ADD_THREAD_MESSAGE", message);
    },

    updateThreadMessage({ commit }, message) {
      commit("UPDATE_THREAD_MESSAGE", message);
    },

    removeThreadMessage({ commit }, messageId) {
      commit("REMOVE_THREAD_MESSAGE", messageId);
    },

    updateThreadMessageReactions({ commit }, { messageId, emojiKey, delta }) {
      commit("UPDATE_THREAD_MESSAGE_REACTIONS", { messageId, emojiKey, delta });
    },

    setGroups({ commit }, payload) {
      if (Array.isArray(payload)) {
        commit("SET_GROUPS", { groups: payload });
      } else {
        commit("SET_GROUPS", payload);
      }
    },

    addGroupToList({ commit }, group) {
      commit("ADD_GROUP_TO_LIST", group);
    },

    updateSidebarGroup({ commit }, { groupId, lastMessage, lastMessageAttachments, senderId, lastMessageTime }) {
      commit("UPDATE_SIDEBAR_GROUP", { groupId, lastMessage, lastMessageAttachments, senderId, lastMessageTime });
    },

    updateGroupInfo({ commit }, { groupId, name, description, avatarUrl, profilePic }) {
      commit("UPDATE_GROUP_INFO", { groupId, name, description, avatarUrl, profilePic });
    },

    removeGroup({ commit }, groupId) {
      commit("REMOVE_GROUP", groupId);
    },

    updateUnreadCount({ commit }, { groupId, unreadCount }) {
      commit("UPDATE_UNREAD_COUNT", { groupId, unreadCount });
    },

    setTypingUser({ commit }, { groupId, userId }) {
      commit("SET_TYPING_USER", { groupId, userId });
      setTimeout(() => {
        commit("CLEAR_TYPING_USER", groupId);
      }, 3000);
    },

    setUnreadIndicator({ commit }, value) {
      commit("SET_UNREAD_INDICATOR", value);
    },

    setActiveChatTab({ commit }, tab) {
      commit("SET_ACTIVE_CHAT_TAB", tab);
    },

    clearUnreadIndicator({ commit }) {
      commit("SET_UNREAD_INDICATOR", false);
    },

    async fetchPinnedGroups({ commit }) {
      try {
        const response = await ChatService.fetchPinnedGroups();
        if (response?.success && response?.pinned_group_ids) {
          commit("SET_PINNED_GROUP_IDS", response.pinned_group_ids);
        }
      } catch (error) {
        console.error("Failed to fetch pinned groups:", error);
      }
    },

    async togglePinGroup({ commit }, groupId) {
      try {
        const response = await ChatService.togglePinGroup(groupId);
        if (response?.success) {
          commit("TOGGLE_PINNED_GROUP", { groupId, pinned: response.pinned });
        }
        return response;
      } catch (error) {
        console.error("Failed to toggle pin:", error);
        throw error;
      }
    },

    setEditingMessage({ commit }, message) {
      commit("SET_EDITING_MESSAGE", message);
    },

    clearEditingMessage({ commit }) {
      commit("SET_EDITING_MESSAGE", null);
    },

    async fetchGroups({ commit }) {
      try {
        const response = await ChatService.fetchGroups({ limit: 500, offset: 0 });
        if (response?.success) {
          const allGroups = response?.conversations || response?.groups || [];
          // Filter out I-Assistant twin groups — they are only accessible via the I-Assistant tab
          const groups = allGroups.filter(g => !g.name?.startsWith("I-Assistant"));
          commit("SET_GROUPS", { groups });
          return { success: true, groups };
        }
        return { success: false };
      } catch (error) {
        console.error("Failed to fetch groups:", error);
        return { success: false, error: error.message };
      }
    },
  },

  getters: {
    newChat: (state) => state.newChat,
    newChatUserList: (state) => state.newChat?.users || [],
    isUserSelected: (state) => (userID) => {
      return state.newChat.users.some((u) => u.id === userID);
    },
    threadStatus: (state) => state.threadStatus,
    isThreadOpen: (state) => state.threadStatus.isOpen,
    threadMessageID: (state) => state.threadStatus.messageID,
    threadId: (state) => state.threadStatus.threadId,
    threadOpen: (state) => !state.threadStatus.isOpen,
    activeChat: (state) => state.activeChat,
    messages: (state) => state.messages,
    messagesLoading: (state) => state.messagesLoading,
    messagesError: (state) => state.messagesError,
    messagesPagination: (state) => state.messagesPagination,
    messageSearchQuery: (state) => state.messageSearchQuery,
    threadMessages: (state) => state.threadMessages,
    threadMessagesLoading: (state) => state.threadMessagesLoading,
    threadMessagesError: (state) => state.threadMessagesError,
    threadMessagesPagination: (state) => state.threadMessagesPagination,
    pendingMessages: (state) => state.pendingMessages,
    pendingReactions: (state) => state.pendingReactions,
    isPendingReaction: (state) => (key) => state.pendingReactions.has(key),
    groups: (state) => state.groups,
    typingUsers: (state) => state.typingUsers,
    typingUserForGroup: (state) => (groupId) => state.typingUsers[groupId],
    frontendUnreadCounts: (state) => state.frontendUnreadCounts,
    frontendUnreadCount: (state) => (groupId) => {
      const normalizedGroupId = String(groupId).replace(/^group_/, '');
      return state.frontendUnreadCounts[normalizedGroupId] || 0;
    },
    hasUnreadIndicator: (state) => state.hasUnreadIndicator,
    editingMessage: (state) => state.editingMessage,
    flashThreadMessageId: (state) => state.flashThreadMessageId,
    isCurrentUserGroupMember: (state) => state.isCurrentUserGroupMember,
    groupMembershipCache: (state) => state.groupMembershipCache,
    chatNotificationSoundEnabled: (state) => state.chatNotificationSoundEnabled,
    lastFetchedChatId: (state) => state.lastFetchedChatId,
    totalUnreadCount: (state) => state.cachedTotalUnreadCount,
    activeChatTab: (state) => state.activeChatTab,
    pinnedGroupIds: (state) => state.pinnedGroupIds,
    isGroupPinned: (state) => (groupId) => state.pinnedGroupIds.has(String(groupId)),
  },
};
